import type {JSONNode} from '@packages/shared/types/JSONNode';
import {Plugins} from '@packages/systems/wfdl';
import {getPluginName} from '@packages/systems/wfdl-Qualified';
import {indexJsonDomNodeArrayById} from './Reification';
import {
  collectElementsWithType,
  getElementTypeAndDataType,
  rootIds,
  rootIdsWith,
} from './rootIds';

const isComponentInstance = (node: JSONNode): boolean =>
  node.type === 'Symbol' && Boolean(node.data?.sym?.inst);

/**
 * How a component instance's definition resolves on the current branch, mirroring
 * rootIds:
 *   - 'missing': no definition on this branch -> drop the instance.
 *   - 'site': a __SitePlugin component -> childIds are the override IDs the
 *     definition still references (so orphaned overrides can be pruned).
 *   - 'other': a library/plugin component with a valid definition -> renderable;
 *     childIds are node.children, exactly as rootIds traverses them (no override
 *     pruning). Without this case such an instance would be misclassified as
 *     'missing' and dropped even though it resolves fine.
 */
type InstanceResolution =
  | {kind: 'missing'}
  | {kind: 'site'; childIds: string[]}
  | {kind: 'other'; childIds: string[]};

/**
 * Display-only filter for CMS Rich Text JSON nodes when branch-scoped component
 * definitions diverge from stored CMS item content.
 *
 * - Skips component instances whose definition is missing on the current branch
 *   (otherwise the Block-atom fallback renders an empty `<div>`).
 * - Skips override content for properties absent from the definition (otherwise
 *   the orphaned nodes surface as plaintext below the component).
 *
 * Instances that still resolve to a valid definition are preserved, including
 * library/plugin components (which resolve under a non-`__SitePlugin` plugin
 * name); only genuinely missing definitions are dropped.
 */
export function filterDivergentRichTextNodes(
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes,
  nodes: JSONNode[]
): {
  jsonContentNodes: JSONNode[];
  rootChildIds: string[];
} {
  // Fast path: with no component instances nothing can diverge, so skip the
  // extra passes below. Returning the original nodes reference preserves array
  // identity for downstream callers that key weakMemo caches on it
  // (e.g. indexJsonDomNodeArrayById in reification).
  if (!nodes.some(isComponentInstance)) {
    return {
      jsonContentNodes: nodes,
      rootChildIds: rootIds(plugins, dataTypes)(nodes),
    };
  }

  const index = indexJsonDomNodeArrayById(nodes);

  // Resolve and cache each instance's classification once per call.
  const instanceResolutionCache = new Map<string, InstanceResolution>();
  const resolveInstance = (node: JSONNode): InstanceResolution => {
    const cached = instanceResolutionCache.get(node._id);
    if (cached) {
      return cached;
    }

    // Callers only invoke this for component instances; this guard mirrors
    // isComponentInstance and narrows node.type / inst to string for
    // getElementTypeAndDataType.
    const inst = node.data?.sym?.inst;
    if (node.type !== 'Symbol' || !inst) {
      return {kind: 'missing'};
    }

    const [elementType, dataType] = getElementTypeAndDataType(
      plugins,
      dataTypes,
      node.type,
      inst
    );

    let resolution: InstanceResolution;
    if (!dataType) {
      resolution = {kind: 'missing'};
    } else if (getPluginName(elementType) === '__SitePlugin') {
      resolution = {
        kind: 'site',
        childIds: collectElementsWithType(node.data?.sym?.overrides, dataType),
      };
    } else {
      resolution = {kind: 'other', childIds: node.children ?? []};
    }

    instanceResolutionCache.set(node._id, resolution);
    return resolution;
  };

  const getRenderableChildIds = (node: JSONNode): string[] | undefined => {
    if (!isComponentInstance(node)) {
      return node.children;
    }
    const resolution = resolveInstance(node);
    return resolution.kind === 'missing' ? [] : resolution.childIds;
  };

  // Override IDs the instance still stores but the definition no longer
  // references are parentless, so rootIdsWith would otherwise promote them to
  // roots and the renderer would print them as plaintext. This covers a missing
  // definition (the whole instance is dropped, so every override is orphaned)
  // and property divergence (only the override IDs the definition no longer
  // references are orphaned). Library/plugin ('other') instances are skipped:
  // rootIds traverses their node.children and never treats overrides as roots.
  const orphanOverrideRootIds = new Set<string>();
  for (const node of nodes) {
    const overrides = node.data?.sym?.overrides;
    if (!isComponentInstance(node) || !overrides) {
      continue;
    }

    const resolution = resolveInstance(node);
    if (resolution.kind === 'other') {
      continue;
    }

    // 'missing' -> empty set, so every override ID is treated as orphaned;
    // 'site' -> only the override IDs the definition still references survive.
    const validChildIdSet = new Set(
      resolution.kind === 'site' ? resolution.childIds : []
    );
    for (const overrideValue of Object.values(overrides)) {
      const overrideIds = Array.isArray(overrideValue)
        ? overrideValue
        : [overrideValue];
      for (const overrideId of overrideIds) {
        if (
          typeof overrideId === 'string' &&
          !validChildIdSet.has(overrideId)
        ) {
          orphanOverrideRootIds.add(overrideId);
        }
      }
    }
  }

  const candidateRootIds = rootIdsWith(getRenderableChildIds)(nodes).filter(
    (rootId) => !orphanOverrideRootIds.has(rootId)
  );

  // Keep only nodes reachable from a valid root through definition-aware child
  // links. Unrenderable instances (and their subtrees) are never reached.
  const reachableNodeIds = new Set<string>();
  const walk = (nodeId: string) => {
    if (reachableNodeIds.has(nodeId)) {
      return;
    }
    const node = index[nodeId];
    // Drop component instances whose definition is missing on this branch (and
    // with them their subtree, which is never reached from a valid root).
    if (
      !node ||
      (isComponentInstance(node) && resolveInstance(node).kind === 'missing')
    ) {
      return;
    }

    reachableNodeIds.add(nodeId);
    for (const childId of getRenderableChildIds(node) ?? []) {
      walk(childId);
    }
  };
  for (const rootId of candidateRootIds) {
    walk(rootId);
  }

  // Nothing diverged: return the original references to keep the reification
  // weakMemo warm and preserve referential stability for callers.
  if (reachableNodeIds.size === nodes.length) {
    return {jsonContentNodes: nodes, rootChildIds: candidateRootIds};
  }

  return {
    jsonContentNodes: nodes.filter((node) => reachableNodeIds.has(node._id)),
    rootChildIds: candidateRootIds.filter((rootId) =>
      reachableNodeIds.has(rootId)
    ),
  };
}
